import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import { Database, ShoppingCart, Users, Cpu, Briefcase, MessageCircle, Truck, RefreshCw, Plus, Trash2, ChevronRight, Sparkles } from "lucide-react";
import AddSourceDialog from "@/components/AddSourceDialog";
import XeroConnectionsPanel from "@/components/XeroConnectionsPanel";
import SquareConnectionPanel from "@/components/SquareConnectionPanel";
import MyobConnectionPanel from "@/components/MyobConnectionPanel";

const ICONS = {
  POS: ShoppingCart, CRM: Users, IOT: Cpu, ERP: Briefcase, SOCIAL: MessageCircle, SUPPLY_CHAIN: Truck,
};
const STATUS_COLORS = {
  ACTIVE: "text-[#00E676] border-[#00E676]/30",
  SYNCING: "text-[#FFB000] border-[#FFB000]/30",
  OFFLINE: "text-[#FF3B30] border-[#FF3B30]/30",
};
const ORIGIN_LABEL = {
  SEEDED: "SAMPLE",
  CSV: "CSV",
  SNOWFLAKE: "SNOWFLAKE",
  SALESFORCE: "SALESFORCE",
  XERO: "XERO",
};

export default function DataSources() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [sources, setSources] = useState([]);
  const [syncing, setSyncing] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = () => api.get("/data-sources").then(r => setSources(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const connected = params.get("connected");
    if (connected === "xero") {
      toast.success("Xero connected. Data is syncing now.");
      params.delete("connected");
      setParams(params, { replace: true });
    }
    const square = params.get("square");
    if (square === "connected") {
      toast.success("Square POS connected. 30-day backfill running…", { duration: 8000 });
      params.delete("square");
      setParams(params, { replace: true });
    }
    const sqErr = params.get("square_error");
    if (sqErr) {
      toast.error(`Square connection failed: ${sqErr}`);
      params.delete("square_error");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = async (id) => {
    setSyncing(id);
    try {
      await api.post(`/data-sources/${id}/sync`);
      await load();
      toast.success("Source synced.");
    } catch { toast.error("Sync failed"); }
    setSyncing(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Disconnect this source? Data will be removed.")) return;
    try {
      await api.delete(`/connectors/${id}`);
      toast.success("Disconnected.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const clearSamples = async () => {
    const count = sources.filter(s => s.is_sample).length;
    if (!count) return;
    if (!window.confirm(`Remove ${count} sample data source${count === 1 ? "" : "s"} and any synthetic insights generated against them? Your real uploads stay.`)) return;
    setClearing(true);
    try {
      const r = await api.delete("/data-sources/sample");
      const n = r.data?.removed_sources || 0;
      toast.success(`Cleared ${n} sample source${n === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't clear samples");
    }
    setClearing(false);
  };

  const stop = (e) => e.stopPropagation();

  const sampleCount = sources.filter(s => s.is_sample).length;
  const realCount = sources.length - sampleCount;

  return (
    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6" data-testid="sources-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · DATA CATALOG</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase mt-1">Data Sources</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl leading-relaxed">
            {realCount > 0
              ? `${realCount} source${realCount === 1 ? "" : "s"} connected.${sampleCount > 0 ? ` ${sampleCount} sample${sampleCount === 1 ? "" : "s"} still here — clear to focus on your data.` : ""}`
              : "Tap + ADD SOURCE to connect Xero, MYOB, Square, or upload a CSV. Sample tiles below show what DARA looks like once your data lands."
            }
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {sampleCount > 0 && (
            <button
              onClick={clearSamples}
              disabled={clearing}
              data-testid="clear-samples-btn"
              className="flex-1 sm:flex-none min-h-[44px] border border-zinc-700 text-zinc-300 hover:border-[#FF3B30]/50 hover:text-[#FF3B30] px-4 py-3 font-mono text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50"
              title="Drop the demo sources & synthetic insights so you only see your real uploaded data"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {clearing ? "CLEARING…" : `CLEAR SAMPLES (${sampleCount})`}
            </button>
          )}
          <button
            onClick={() => setDialog(true)}
            data-testid="add-source-btn"
            style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }}
            className="flex-1 sm:flex-none min-h-[44px] font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 inline-flex items-center justify-center gap-2 transition-opacity"
          >
            <Plus className="w-4 h-4" /> ADD SOURCE
          </button>
        </div>
      </div>

      <XeroConnectionsPanel onMutate={load} />

      <MyobConnectionPanel />

      <SquareConnectionPanel />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sources.map(s => {
          const Icon = ICONS[s.type] || Database;
          const origin = s.origin || "SEEDED";
          const isSample = !!s.is_sample;
          return (
            <Panel
              key={s.id}
              testId={`source-${s.id}`}
              title={`// ${s.type}`}
              meta={
                <div className="flex items-center gap-2">
                  {isSample && (
                    <span data-testid={`sample-badge-${s.id}`} className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5">SAMPLE</span>
                  )}
                  <span className="font-mono text-[9px] text-zinc-500">{ORIGIN_LABEL[origin] || origin}</span>
                  <span className={`font-mono text-[10px] px-2 py-0.5 border ${STATUS_COLORS[s.status]}`}>{s.status}</span>
                </div>
              }
            >
              <div
                className={`p-5 cursor-pointer hover:bg-zinc-900/30 transition-colors ${isSample ? "opacity-70" : ""}`}
                onClick={() => nav(`/app/sources/${s.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === "Enter" || e.key === " ") && nav(`/app/sources/${s.id}`)}
                data-testid={`open-source-${s.id}`}
              >
                <div className="flex items-start gap-3 mb-4">
                  <Icon className="w-5 h-5 text-[#FFB000] stroke-[1.5]" />
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-base flex items-center gap-1.5">
                      {s.name}
                      <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-[#FFB000] transition-colors" />
                    </h3>
                    <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{s.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">RECORDS</div>
                    <div className="font-mono text-xl text-white mt-1">{s.records_count.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">HEALTH</div>
                    <div className={`font-mono text-xl mt-1 ${s.health_score >= 90 ? "text-[#00E676]" : s.health_score >= 75 ? "text-[#FFB000]" : "text-[#FF3B30]"}`}>{s.health_score}%</div>
                  </div>
                </div>

                <div className="h-1 bg-zinc-900 mb-4">
                  <div className="h-full bg-[#FFB000]" style={{ width: `${s.health_score}%` }} />
                </div>

                {s.profile && (
                  <div className="border border-zinc-900 px-3 py-2 mb-3 flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-[#FFB000]" />
                    <span className="font-mono text-[10px] text-zinc-400">
                      {s.profile.columns?.length || 0} columns profiled · {s.profile.row_count?.toLocaleString() || 0} rows
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between gap-2" onClick={stop}>
                  <span className="font-mono text-[10px] text-zinc-600 truncate">
                    SYNC: {new Date(s.last_sync).toLocaleString()}
                  </span>
                  <div className="flex gap-1">
                    {!isSample && (
                      <button
                        onClick={(e) => { stop(e); sync(s.id); }}
                        disabled={syncing === s.id}
                        data-testid={`sync-${s.id}`}
                        className="border border-zinc-800 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest hover:text-[#FFB000] hover:border-[#FFB000]/50 transition-colors flex items-center gap-1 disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3 h-3 ${syncing === s.id ? "animate-spin" : ""}`} />
                        {syncing === s.id ? "SYNCING" : "SYNC"}
                      </button>
                    )}
                    <button
                      onClick={(e) => { stop(e); remove(s.id); }}
                      data-testid={`delete-${s.id}`}
                      className="border border-zinc-800 px-2 py-1 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 transition-colors"
                      title="Disconnect"
                    ><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>
            </Panel>
          );
        })}
      </div>

      <AddSourceDialog open={dialog} onClose={() => setDialog(false)} onDone={load} />
    </div>
  );
}
